import { Columns3 } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { Checkbox } from "@/components/ui/Checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/Popover";

import { type TColumnDef } from "./types";

type TDataGridColumnSelectorProps<TRow> = {
  columns: TColumnDef<TRow>[];
  visibility: Record<string, boolean>;
  onChange: (visibility: Record<string, boolean>) => void;
};

/** Popover checklist to toggle visibility of hideable columns. */
export function DataGridColumnSelector<TRow>({
  columns,
  visibility,
  onChange,
}: TDataGridColumnSelectorProps<TRow>) {
  const hideable = columns.filter((c) => c.hideable !== false);

  const toggle = (id: string, checked: boolean) => {
    onChange({ ...visibility, [id]: checked });
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          aria-label="Toggle columns"
        >
          <Columns3 className="mr-2 h-4 w-4" />
          Columns
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-56 p-2"
        align="end"
      >
        <p className="px-2 py-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Toggle columns
        </p>
        <ul className="max-h-72 overflow-y-auto">
          {hideable.map((column) => {
            const checked = visibility[column.id] !== false;
            const label = typeof column.header === "string" ? column.header : column.id;
            return (
              <li key={column.id}>
                <label className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent">
                  <Checkbox
                    checked={checked}
                    onCheckedChange={(state) => toggle(column.id, state === true)}
                    aria-label={`Toggle ${label} column`}
                  />
                  <span className="truncate text-foreground">{label}</span>
                </label>
              </li>
            );
          })}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
